<template>
  <section
    class="ticket-card"
    :class="{ 'ticket-card--cancelled': isCancelled }"
  >
    <div class="ticket-card__travel-date-time">
      <span>{{ departureDate }}</span>
      <span>{{ departureTime }}-{{ arrivalTime }}</span>
    </div>
    <h2 class="ticket-card__heading">
      <div
        v-if="isCancelled"
        class="ticket-card__cancelled-label"
      >
        {{ $t('ticketCard.cancelled') }}
      </div>
      <div class="ticket-card__heading-row">
        <span>
          {{ originStation }}
          <span v-if="departurePlatform" class="ticket-card__platform">
            ({{ $t('ticketCard.platform') }}: {{ departurePlatform }})
          </span>
        </span>
        <svg class="ticket-card__arrow">
          <use :xlink:href="`#${arrow}`" />
        </svg>
        <span class="ticket-card__heading-destination">
          {{ destinationStation }}
          <span v-if="arrivalPlatform" class="ticket-card__platform">
            ({{ $t('ticketCard.platform') }}: {{ arrivalPlatform }})
          </span>
        </span>
      </div>
      <div
        v-if="isRoundTrip"
        class="ticket-card__heading-row ticket-card__heading-row--inbound"
      >
        <span>
          {{ destinationStation }}
          <span v-if="returnDeparturePlatform" class="ticket-card__platform">
            ({{ $t('ticketCard.platform') }}: {{ returnDeparturePlatform }})
          </span>
        </span>
        <svg class="ticket-card__arrow">
          <use :xlink:href="`#${arrow}`" />
        </svg>
        <span class="ticket-card__heading-destination">
          {{ originStation }}
          <span v-if="returnArrivalPlatform" class="ticket-card__platform">
            ({{ $t('ticketCard.platform') }}: {{ returnArrivalPlatform }})
          </span>
        </span>
      </div>
      <div v-if="routeReference" class="ticket-card__route-reference">
        {{ routeReference }}
      </div>
    </h2>
    <p class="ticket-card__bookingnumber">
      {{ $t('ticketCard.bookingNumber') }}: {{ bookingNumber }}
    </p>
    <div
      v-if="!isCancelled"
      class="ticket-card__ticket-link-wrapper"
    >
      <router-link
        class="ticket-card__ticket-link"
        :to="{
          name: 'ticketPage',
          params: { bookingNumber: bookingNumber },
        }"
      >
        {{ $t('ticketCard.showTicket') }}
        <svg>
          <use :xlink:href="`#${chevron}`" />
        </svg>
      </router-link>
    </div>
  </section>
</template>

<script lang="ts">
  import { defineComponent, computed, PropType } from 'vue'
  import arrow from '@/assets/icons/arrow.svg'
  import chevron from '@/assets/icons/chevron.svg'

  interface Segment {
  direction: 'OUTBOUND' | 'INBOUND';
  service_name: string;
  travel_date: string;
  departure_station: {
    name: string;
    departure_platform?: string | null;
  };
  arrival_station: {
    name: string;
    arrival_platform?: string | null;
  };
  meta_data?: Array<{
    key: string;
    value: string;
  }>;
}

  export default defineComponent({
    name: 'TicketCard',
    props: {
      bookingNumber: {
        type: String,
        required: true,
      },
      departureDate: {
        type: String,
        required: true,
      },
      departureTime: {
        type: String,
        required: true,
      },
      arrivalTime: {
        type: String,
        required: true,
      },
      originStation: {
        type: String,
        required: true,
      },
      destinationStation: {
        type: String,
        required: true,
      },
      segments: {
        type: Array as PropType<Segment[]>,
        default() {
          return []
        },
      },
      bookingDetails: {
        type: Object,
        default: null,
      },
      isCancelled: {
        type: Boolean,
        default: false,
      },
      hasDetails: {
        type: Boolean,
        default: false,
      },
      isLoadingDetails: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const hasDetailedPassengers = computed(() => {
        return !!props.bookingDetails?.passengers?.length && 
          props.bookingDetails.passengers.some((p: any) => p.first_name || p.last_name)
      })

      const formatBirthDate = (dateString: string) => {
        if (!dateString) return ''
        try {
          const date = new Date(dateString)
          return date.toLocaleDateString()
        } catch (e) {
          return dateString
        }
      }

      const isRoundTrip = computed(() => {
        const outboundSegments = props.segments.filter(
          (s: { direction: 'OUTBOUND' | 'INBOUND' }) => s.direction === 'OUTBOUND'
        )
        const inboundSegments = props.segments.filter(
          (s: { direction: 'OUTBOUND' | 'INBOUND' }) => s.direction === 'INBOUND'
        )
        return inboundSegments.length > 0 && outboundSegments.length > 0
      })

      // Platform information for outbound trip
      const departurePlatform = computed(() => {
        const outboundSegment = props.segments.find(
          (s: { direction: 'OUTBOUND' | 'INBOUND' }) => s.direction === 'OUTBOUND'
        )
        return outboundSegment?.departure_station?.departure_platform || ''
      })

      const arrivalPlatform = computed(() => {
        const outboundSegment = props.segments.find(
          (s: { direction: 'OUTBOUND' | 'INBOUND' }) => s.direction === 'OUTBOUND'
        )
        return outboundSegment?.arrival_station?.arrival_platform || ''
      })

      // Platform information for inbound trip (if round trip)
      const returnDeparturePlatform = computed(() => {
        const inboundSegment = props.segments.find(
          (s: { direction: 'OUTBOUND' | 'INBOUND' }) => s.direction === 'INBOUND'
        )
        return inboundSegment?.departure_station?.departure_platform || ''
      })

      const returnArrivalPlatform = computed(() => {
        const inboundSegment = props.segments.find(
          (s: { direction: 'OUTBOUND' | 'INBOUND' }) => s.direction === 'INBOUND'
        )
        return inboundSegment?.arrival_station?.arrival_platform || ''
      })

      const routeReference = computed(() => {
        const outboundSegment = props.segments.find(
          (s: { direction: 'OUTBOUND' | 'INBOUND' }) => s.direction === 'OUTBOUND'
        )
        if (!outboundSegment?.meta_data) return ''
        const routeCode = outboundSegment.meta_data.find(
          (meta) => meta.key === 'route_code'
        )?.value || ''
        const routeName = outboundSegment.meta_data.find(
          (meta) => meta.key === 'route_name'
        )?.value || ''
        return routeCode && routeName ? `${routeCode} ${routeName}` : routeCode || routeName || ''
      })

      return {
        arrow,
        chevron,
        isRoundTrip,
        hasDetailedPassengers,
        formatBirthDate,
        departurePlatform,
        arrivalPlatform,
        returnDeparturePlatform,
        returnArrivalPlatform,
        routeReference,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .ticket-card {
    @include body-large;
    display: flex;
    flex-direction: column;
    padding: rem(30) 30px;
    color: $clr-text;
    background-color: $clr-white;
    border-top: 8px solid $clr-secondary;
    border-radius: $border-radius;
    box-shadow: $box-shadow-card;

    &--cancelled {
      border-top-color: $clr-text-disable;
    }

    &__travel-date-time {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-bottom: rem(16);

      @media screen and (min-width: $breakpoint-xs) {
        flex-direction: row;
      }
    }

    &__heading {
      @include title-large;
      display: flex;
      flex-direction: column;
      margin-bottom: rem(20);
      color: $clr-primary;
      gap: 8px;
      text-align: left;

      @media screen and (max-width: $breakpoint-xs) {
        font-size: rem(20);
      }

      &__cancelled-label {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: var(--clr-text-disabled);
        font-weight: var(--font-weight-bold);
        font-size: var(--font-size-medium);
        z-index: 1;
        background-color: var(--clr-white);
        padding: 0.5rem 1rem;
        border-radius: var(--border-radius-small);
        box-shadow: var(--box-shadow-small);
      }

      &-row {
        display: flex;
        align-items: center;
        white-space: nowrap;
        flex-wrap: wrap;
        column-gap: 8px;
      }

      &-destination {
        display: block;
      }
    }

    &__arrow {
      flex-shrink: 0;
      transform: rotate(180deg);
      width: 16px;
      height: 16px;
      margin-top: 6px;
      color: $clr-secondary;
    }

    &__bookingnumber {
      text-align: left;
      margin-bottom: rem(30);
    }

    &__ticket-link {
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      text-decoration: none;
      color: $clr-primary;

      &-wrapper {
        display: flex;
        justify-content: flex-end;
        padding-top: rem(30);
      }

      svg {
        transform: rotate(90deg);
        width: 16px;
        height: 16px;
        color: $clr-primary;
      }
    }

    &__platform {
      @include body-small;
      color: $clr-text;
      margin-left: 0.5rem;
    }

    &__route-reference {
      @include body-medium;
      color: $clr-text;
      margin-top: 0.5rem;
      text-align: left;
    }
  }
</style>